/**
 * Election Hub Type Definitions
 * Union types for type-safe US state codes and party affiliations
 */

import type { ForecastTraderElectionGridMatchedContract } from '@/types/forecasttrader.types';

// US State codes - union type for type safety (not string)
export type USStateCode =
  | 'AL'
  | 'AK'
  | 'AZ'
  | 'AR'
  | 'CA'
  | 'CO'
  | 'CT'
  | 'DE'
  | 'FL'
  | 'GA'
  | 'HI'
  | 'ID'
  | 'IL'
  | 'IN'
  | 'IA'
  | 'KS'
  | 'KY'
  | 'LA'
  | 'ME'
  | 'MD'
  | 'MA'
  | 'MI'
  | 'MN'
  | 'MS'
  | 'MO'
  | 'MT'
  | 'NE'
  | 'NV'
  | 'NH'
  | 'NJ'
  | 'NM'
  | 'NY'
  | 'NC'
  | 'ND'
  | 'OH'
  | 'OK'
  | 'OR'
  | 'PA'
  | 'RI'
  | 'SC'
  | 'SD'
  | 'TN'
  | 'TX'
  | 'UT'
  | 'VT'
  | 'VA'
  | 'WA'
  | 'WV'
  | 'WI'
  | 'WY'
  | 'DC';

// Standardize on 'GOP' throughout (not 'REP')
export type PartyCode = 'GOP' | 'DEM' | 'IND' | 'GRN';

// Election contract for a state race
export interface ElectionContract {
  productConid: number; // Product contract ID for filtering and identification (market-level API calls)
  stateCode: USStateCode;
  name: string;
  favorite?: PartyCode; // Leading party from API — undefined = toss-up
  price: number; // Cents (0-100)
  volume: string; // Trading volume information
  openInterest: number; // Open interest count
  district?: string; // For House races
  leadingDemConid?: number; // Leading Democrat candidate "yes" conid from API
  leadingRepConid?: number; // Leading Republican candidate "yes" conid from API
}

// Updated Election Contract interface to match API response and UI requirements
export interface ElectionContractFromAPI {
  // Product-level information (from Election Grid API)
  productConid: number; // Event product contract ID
  state: string; // State name from API
  stateCode: USStateCode; // Derived/mapped state code
  office: 'governor' | 'senate' | 'house'; // Office type for this race

  // Individual contract information (from contracts array in API)
  contracts: Array<{
    conid_yes: number; // Yes contract ID for trading
    conid_no: number; // No contract ID for trading
    strike_label: string; // Display label (contains candidate name)
    exchange: string; // Trading exchange
    party_affiliation: string; // Party affiliation, raw value from exchange
    matched?: ForecastTraderElectionGridMatchedContract[]; // Same candidate on other exchanges
  }>;

  // UI-specific derived data
  name: string; // Formatted race name (e.g., "Arizona Senate")
  raceType: 'Governor' | 'Senate' | 'House'; // Formatted for UI
  district?: string; // For House races

  // Market data (to be populated from MD API or derived)
  price?: number; // Leading candidate price in cents
  favorite?: PartyCode; // Leading party
  favoriteColor?: number; // Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid)
  volume?: string; // Trading volume
  openInterest?: number; // Open interest

  // API-provided leader and timing
  leadingCandidateConid?: number; // Leading candidate "yes" conid from API
  leadingDemConid?: number; // Leading Democrat candidate "yes" conid from API
  leadingRepConid?: number; // Leading Republican candidate "yes" conid from API
  lastTradeTime?: number; // Last trade time in epoch seconds from API
  measuredPeriod?: string; // FTAPI-231: service-provided period (e.g. "Nov'26"), preferred over lastTradeTime for display when present

  // Trading dates
  tradingCloses?: Date; // When trading closes for this race
}

// Individual candidate derived from Election Grid API contracts
export interface ElectionCandidate {
  // Candidate identification
  name: string; // Parsed from strike_label
  title: string; // Position title (e.g., "Democrat", "Republican")
  party: PartyCode; // Derived from strike_label or title
  /** Full party affiliation label from API (e.g., "Libertarian", "Green", "Democrat") */
  partyAffiliation: string;
  photo: string; // URL for candidate photo

  // Trading information
  conid_yes: number; // Yes contract ID
  conid_no: number; // No contract ID
  exchange: string; // Trading exchange
  strike_label: string; // Original strike label from API
  matched?: ForecastTraderElectionGridMatchedContract[]; // Same candidate on other exchanges

  // Market data (populated from MD API)
  price: number; // Current market price in cents
  volume?: string; // Trading volume
  openInterest?: number; // Open interest for this candidate

  // Race context
  raceId: string; // Race identifier for grouping
  productConid: number; // Product contract ID for market-level API calls
  stateCode: USStateCode; // State code
  raceName: string; // Full race name
  raceType: 'Governor' | 'Senate' | 'House'; // Race type
  district?: string; // For House races

  // API-provided leader and timing
  raceFavorite?: PartyCode; // Product-level leading party from API — undefined = toss-up
  raceColor?: number; // Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid)
  leadingCandidateConid?: number; // Leading candidate "yes" conid from API (deprecated — may not be present)
  leadingDemConid?: number; // Leading Democrat candidate "yes" conid from API
  leadingRepConid?: number; // Leading Republican candidate "yes" conid from API
  lastTradeTime: number; // Last trade time in epoch seconds from API
  measuredPeriod?: string; // FTAPI-231: service-provided period (e.g. "Nov'26"), preferred over lastTradeTime for display when present

  // Additional metadata
  isIncumbent?: boolean; // Whether candidate is incumbent
  isLoadingPrice?: boolean; // Whether price is being fetched
}

// Candidate information for detail views
export interface CandidateInfo {
  name: string;
  title: string;
  price: number;
  photo: string;
  detail: string;
  party: PartyCode;
  isIncumbent?: boolean;
  // Trading contract IDs for bid-ask price fetching
  conid_yes?: number;
  conid_no?: number;
}

// Candidates for a state (dem, gop, optional ind)
export interface StateCandidates {
  dem: CandidateInfo;
  gop: CandidateInfo;
  ind?: CandidateInfo;
}

// Individual candidate based on actual strike_label
export interface ActualCandidate {
  /** Actual candidate name from strike_label */
  name: string;
  /** Party affiliation determined from strike_label */
  party: PartyCode;
  /** Full party affiliation label from API (e.g., "Libertarian", "Green", "Democrat") */
  partyAffiliation: string;
  /** Current contract price (ask price if available, fallback to strike) */
  price: number;
  /** Contract identifier for trading */
  conid: number;
  conid_no: number;
  /** Contract exchange for WebSocket subscriptions */
  exchange: string;
  /** All exchanges this candidate trades on (only populated when multi-exchange) */
  exchanges?: string[];
  /** "conid@exchange" pairs for yes contracts across all exchanges (only when multi-exchange) */
  yesConidExPairs?: string[];
  /** "conid@exchange" pairs for no contracts across all exchanges (only when multi-exchange) */
  noConidExPairs?: string[];
  /** Contract side (Y or N) */
  side: string;
  /** Original strike_label from backend */
  strike_label: string;
  /** Photo URL for candidate */
  photo: string;
  /** Additional details */
  detail: string;
  /** Whether this candidate is an incumbent */
  isIncumbent?: boolean;
  /** Whether the price is currently loading from ask price API */
  isLoadingPrice?: boolean;
  /** Whether the price is using ask price (true) or fallback to strike (false) */
  usingAskPrice?: boolean;
}

// All actual candidates for a race (dynamic list based on strikes)
export interface ActualCandidates {
  /** All candidates from strike_label data */
  candidates: ActualCandidate[];
  /** Leading candidate based on highest price */
  leading?: ActualCandidate;
}

// Election type filter options
export type ElectionType = 'Governor' | 'House' | 'Senate' | 'All By State';

// View mode for grid display
export type ViewMode = 'card' | 'table';

// Sort options for election list
export type SortOption = 'alphabetical' | 'open-interest';

// Chart display mode
export type ChartMode = 'line' | 'area';

/**
 * Candidate row for table view - flattened from ElectionContract
 * Each race produces 2-3 CandidateTableRow entries (one per candidate)
 */
export interface CandidateTableRow {
  // Race info (for grouping)
  raceId: string; // `${stateCode}-${electionType}-${district?}`
  productConid: number; // Product contract ID for market-level API calls
  stateCode: USStateCode;
  raceName: string;
  raceType: 'Governor' | 'House' | 'Senate';
  district?: string;
  racePrice: number; // Leading party price
  raceFavorite?: PartyCode; // Leading party from API — undefined = toss-up
  raceColor?: number; // Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid)
  leadingCandidateConid?: number; // Leading candidate "yes" conid from API (deprecated — may not be present)
  leadingDemConid?: number; // Leading Democrat candidate "yes" conid from API
  leadingRepConid?: number; // Leading Republican candidate "yes" conid from API
  lastTradeTime: number; // Last trade time in epoch seconds from API
  measuredPeriod?: string; // FTAPI-231: service-provided period (e.g. "Nov'26"), preferred over lastTradeTime for display when present

  // Candidate info
  candidateName: string;
  candidateTitle: string;
  candidatePhoto: string;
  party: PartyCode;
  /** Full party affiliation label from API (e.g., "Libertarian", "Green", "Democrat") */
  partyAffiliation: string;
  price: number; // Candidate's price
  volume: string; // Derived per-candidate
  openInterest: number; // Derived per-candidate

  // Trading contract IDs for bid-ask price fetching
  conid_yes: number; // Yes contract ID
  conid_no: number; // No contract ID
  exchange: string; // Trading exchange
  matched?: ForecastTraderElectionGridMatchedContract[]; // Same candidate on other exchanges
}

// Time range for price history
export type TimeRange = '1D' | '1W' | '1M' | '3M' | '6M' | 'ALL';

// Color constants for party affiliations
export const PARTY_COLORS = {
  GOP_DARK: '#D22718',
  GOP_MID: '#E6756C',
  GOP_LIGHT: '#F4BDB8',
  DEM_DARK: '#2349F5',
  DEM_MID: '#6B8CFD',
  DEM_LIGHT: '#C1D1FF',
  IND_DARK: '#9333EA', // Purple for independent party
  GRN_DARK: '#16a34a',
  GRN_MID: '#4ade80',
  GRN_LIGHT: '#bbf7d0',
  TOSS_UP: '#EBD568', // Yellow for toss-up races (no candidate >= 55%)
  NEUTRAL: '#D0D1D2', // Light theme neutral
  NEUTRAL_DARK: '#383B41', // Dark theme neutral
} as const;

// Election Hub UI colors
export const ELECTION_COLORS = {
  /** Gold accent color for title highlights (e.g., "Dashboard", "Election Grid") */
  TITLE_GOLD: 'oklch(68.1% .162 75.834)',
} as const;

// FIPS code to state code mapping for D3 TopoJSON
export const FIPS_TO_STATE: Record<string, USStateCode> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY',
};

// State code to FIPS mapping (reverse lookup)
export const STATE_TO_FIPS: Record<USStateCode, string> = Object.entries(FIPS_TO_STATE).reduce(
  (acc, [fips, state]) => {
    acc[state] = fips;
    return acc;
  },
  {} as Record<USStateCode, string>,
);

// State code to full name mapping
export const STATE_NAMES: Record<USStateCode, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

// Type guard for USStateCode
export function isUSStateCode(code: string): code is USStateCode {
  return code in STATE_NAMES;
}

// Type guard for PartyCode
export function isPartyCode(code: string): code is PartyCode {
  return code === 'GOP' || code === 'DEM' || code === 'IND' || code === 'GRN';
}

// Leading candidate information for state summary rows
export interface LeadingCandidateInfo {
  name: string;
  party: PartyCode;
  /** Full party affiliation label (e.g., "Libertarian", "Green", "Democrat") */
  partyAffiliation: string;
  price: number;
  conid: number;
}

// State row summary for hierarchical table
export interface StateRowSummary {
  raceId: string;
  productConid: number; // Product contract ID to differentiate multiple products per state
  stateCode: USStateCode;
  stateName: string;
  name: string; // Product display name from API
  raceType: 'Governor' | 'Senate' | 'House';
  district?: string;
  leadingCandidate: LeadingCandidateInfo;
  tradingCloses: Date;
  candidateCount: number;
}
